/**
 * O prazo de uma ação, na coluna da tabela (issue #359, D10).
 *
 * O prazo nunca é digitado: vem calculado da data de elaboração do plano mais a prioridade. O
 * chip é o único lugar da tela que diz se aquele dia já passou.
 */

import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  CheckCircle as NoPrazoIcon,
  Error as VencidaIcon,
  PriorityHigh as ImediatoIcon,
  Warning as AVencerIcon,
} from '@mui/icons-material';

import {
  diasAteOPrazo,
  formatarDia,
  situacaoDoPrazo,
  type ActionItemStatus,
  type SituacaoDoPrazo,
} from '../../../types/actionPlan';

export interface DueDateBadgeProps {
  /** `YYYY-MM-DD`, ou nulo quando a prioridade é intolerável. */
  dueDate: string | null;
  status: ActionItemStatus;
  /** Só os testes passam este valor: a tela pergunta o dia de hoje ao relógio. */
  hoje?: Date;
}

const CORES: Record<SituacaoDoPrazo, 'error' | 'warning' | 'success' | 'default'> = {
  IMEDIATO: 'error',
  VENCIDA: 'error',
  A_VENCER: 'warning',
  NO_PRAZO: 'success',
  ENCERRADA: 'default',
};

const ICONES: Record<SituacaoDoPrazo, React.ReactElement | undefined> = {
  IMEDIATO: <ImediatoIcon />,
  VENCIDA: <VencidaIcon />,
  A_VENCER: <AVencerIcon />,
  NO_PRAZO: <NoPrazoIcon />,
  ENCERRADA: undefined,
};

function emDias(quantidade: number): string {
  return `${quantidade} ${quantidade === 1 ? 'dia' : 'dias'}`;
}

export const DueDateBadge: React.FC<DueDateBadgeProps> = ({ dueDate, status, hoje }) => {
  const referencia = hoje ?? new Date();
  const situacao = situacaoDoPrazo({ dueDate, status }, referencia);

  // Sem data não há dia para formatar nem contagem para fazer, e isso vale em toda situação: a
  // ação intolerável continua sem prazo depois de concluída ou cancelada. Encerrada, o chip
  // perde o alarme e mantém a palavra, para não pedir de volta o que já foi feito.
  if (dueDate === null) {
    const encerrada = situacao === 'ENCERRADA';
    return (
      <Tooltip
        title={
          encerrada
            ? 'Prioridade intolerável: a ação não esperava prazo'
            : 'Prioridade intolerável: a ação não espera prazo'
        }
        arrow
      >
        <Chip
          size="small"
          color={CORES[situacao]}
          variant={encerrada ? 'outlined' : 'filled'}
          icon={ICONES[situacao]}
          label="Imediato"
        />
      </Tooltip>
    );
  }

  const dia = formatarDia(dueDate);
  const dias = diasAteOPrazo(dueDate, referencia);

  const rotulo =
    situacao === 'VENCIDA'
      ? `Vencida há ${emDias(Math.abs(dias))}`
      : situacao === 'A_VENCER'
        ? `Vence em ${emDias(dias)}`
        : dia;

  const explicacao =
    situacao === 'ENCERRADA' ? `Prazo era ${dia}` : `Prazo: ${dia}`;

  return (
    <Tooltip title={explicacao} arrow>
      <Chip
        size="small"
        color={CORES[situacao]}
        variant={situacao === 'NO_PRAZO' ? 'outlined' : 'filled'}
        icon={ICONES[situacao]}
        label={rotulo}
      />
    </Tooltip>
  );
};

export default DueDateBadge;
